import styled, { css } from 'styled-components'
import { colors } from '@/styles'
import Icon from '@/components/icon'

type Theme = 'error' | 'info' | 'warning' | 'success' | 'blue'
type Space = 'fit' | 'normal'
type ContentAlign = 'flexStart'
type IconAlign = 'center'

const error = css`
  background: ${colors.red96};
  border: 1px solid ${colors.red88};
  color: ${colors.red32};
  --title-color: ${colors.red16};
  --icon-color: ${colors.red48};
`

const info = css`
  background: ${colors.blue96};
  border: 1px solid ${colors.blue88};
  color: ${colors.blue32};
  --title-color: ${colors.blue16};
  --icon-color: ${colors.blue48};
`

const warning = css`
  background: ${colors.yellow96};
  border: 1px solid ${colors.yellow88};
  color: ${colors.yellow32};
  --title-color: ${colors.yellow16};
  --icon-color: ${colors.yellow48};
`

const success = css`
  background: ${colors.green96};
  border: 1px solid ${colors.green88};
  color: ${colors.green24};
  --title-color: ${colors.green16};
  --icon-color: ${colors.green40};
`
const blue = css`
  background: ${colors.blue96};
  border: 1px solid ${colors.blue88};
  color: ${colors.green24};
  --title-color: ${colors.blue16};
  --icon-color: ${colors.blue48};
`

const Wrapper = styled.div<StyledProps>`
  display: flex;

  flex-shrink: 0;
  overflow: hidden;
  z-index: 1;
  padding: 16px;
  ${props =>
    props.space === 'normal' &&
    `
    margin: 0 24px 24px 24px;
  `}
  gap: 14px;
  border-radius: 6px;
  position: relative;
  &[aria-hidden='true'] {
    display: none;
  }

  ${props => getVariantStyles(props)}
`

const Content = styled.div<StyledProps>`
  display: flex;
  flex-direction: column;
  ${props =>
    props.contentAlign === 'flexStart' &&
    `
    flex-direction: 'column';
    align-items: flex-start;
  `}
  gap: 8px;
  width: 100%;
`

const Title = styled.div`
  color: var(--title-color);
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
`

const Body = styled.div`
  color: inherit;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
`

const IconWrapper = styled.div<StyledProps>`
  flex: 0 0 16px;
  ${props =>
    props.iconAlign === 'center' &&
    `
    display: flex;
    align-items: center;
  `}
  color: var(--icon-color);
`

const getVariantStyles = (props: StyledProps) => {
  if (props.theme === 'error') return error
  if (props.theme === 'warning') return warning
  if (props.theme === 'success') return success
  if (props.theme === 'blue') return blue
  return info
}

type StyledProps = {
  theme?: Theme
  space?: Space
  contentAlign?: ContentAlign
  iconAlign?: IconAlign
}

type ModalAlertProps = StyledProps & {
  title?: React.ReactNode
  body?: React.ReactNode
  icon?: React.ReactNode
  isShow?: boolean
}
// Looks like we bold the text if no title is present (+ different color)
const ModalAlert: React.FC<ModalAlertProps> = ({
  title,
  body,
  icon,
  isShow = true,
  theme,
  space = 'normal',
  contentAlign,
  iconAlign,
  ...rest
}) => {
  if (!isShow) return <></>
  return (
    <Wrapper theme={theme} space={space} {...rest}>
      <IconWrapper iconAlign={iconAlign}>
        {icon && <Icon id={icon} width={16} height={16} />}
      </IconWrapper>
      <Content contentAlign={contentAlign}>
        {typeof title !== 'undefined' ? (
          <>
            <Title>{title}</Title>
            <Body>{body}</Body>
          </>
        ) : (
          <Title>{body}</Title>
        )}
      </Content>
    </Wrapper>
  )
}

export default ModalAlert
